/**
 * Shopify's rich text fields can arrive as their JSON document rather than as
 * HTML. The markup renders the raw document escaped and flags it; this turns it
 * into HTML before anything measures the element for the "Read more" clamp.
 *
 * Parsing in the browser rather than in Liquid means the document's own escapes
 * — a quoted word inside a review, say — survive correctly.
 */
function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function richTextNodeToHtml(node) {
  if (!node) return '';

  switch (node.type) {
    case 'root':
      return richTextChildrenToHtml(node);

    case 'paragraph':
      return `<p>${richTextChildrenToHtml(node)}</p>`;

    case 'heading': {
      const level = Math.min(Math.max(Number(node.level) || 2, 1), 6);
      return `<h${level}>${richTextChildrenToHtml(node)}</h${level}>`;
    }

    case 'list': {
      const tag = node.listType === 'ordered' ? 'ol' : 'ul';
      return `<${tag}>${richTextChildrenToHtml(node)}</${tag}>`;
    }

    case 'list-item':
      return `<li>${richTextChildrenToHtml(node)}</li>`;

    case 'link': {
      // Only http(s) and mailto, so a migrated review cannot carry javascript:.
      const url = String(node.url || '');
      const safe = /^(https?:|mailto:|\/)/i.test(url) ? url : '';
      const title = node.title ? ` title="${escapeHtml(node.title)}"` : '';
      if (!safe) return richTextChildrenToHtml(node);
      return `<a href="${escapeHtml(safe)}"${title} target="_blank" rel="noopener nofollow">${richTextChildrenToHtml(node)}</a>`;
    }

    case 'text': {
      let text = escapeHtml(node.value).replace(/\n/g, '<br>');
      if (node.bold) text = `<strong>${text}</strong>`;
      if (node.italic) text = `<em>${text}</em>`;
      return text;
    }

    default:
      return richTextChildrenToHtml(node);
  }
}

function richTextChildrenToHtml(node) {
  if (!node || !Array.isArray(node.children)) return '';
  return node.children.map(richTextNodeToHtml).join('');
}

function renderRichTextFields(root) {
  root.querySelectorAll('[data-rich-json]').forEach((el) => {
    const raw = el.textContent.trim();
    if (!raw) return;

    let doc;
    try {
      doc = JSON.parse(raw);
    } catch (_) {
      // Not a document after all; leave the text as it is.
      return;
    }

    const html = richTextNodeToHtml(doc);
    if (html) el.innerHTML = html;
    el.removeAttribute('data-rich-json');
  });
}

// Applies rich-text conversion and the "Read more" clamp to a set of cards.
function enhanceReviewCards(root) {
  renderRichTextFields(root);

  root.querySelectorAll('[data-truncate-target]').forEach((textEl) => {
    if (textEl.dataset.truncateReady === 'true') return;
    textEl.dataset.truncateReady = 'true';
    textEl.classList.add('is-truncated');
    setTimeout(() => {
      if (textEl.scrollHeight > textEl.clientHeight) {
        const triggerBtn = textEl.nextElementSibling;
        if (triggerBtn && triggerBtn.hasAttribute('data-truncate-trigger')) {
          triggerBtn.style.display = 'inline-block';
          triggerBtn.addEventListener('click', () => {
            if (textEl.classList.contains('is-truncated')) {
              textEl.classList.remove('is-truncated');
              triggerBtn.textContent = 'Read less';
            } else {
              textEl.classList.add('is-truncated');
              triggerBtn.textContent = 'Read more';
            }
          });
        }
      } else {
        textEl.classList.remove('is-truncated');
      }
    }, 100);
  });
}

// ---------------------------------------------------------------------------
// Storefront API loader — the reliable way to get ALL reviews into the browser.
// Shopify Liquid caps a page template at ~250 metaobjects and `paginate` does
// not page correctly outside collection templates, so we query the Storefront
// GraphQL API (cursor pagination, 250 per request) instead.
// ---------------------------------------------------------------------------

// Mirrors Liquid's `handle` filter closely enough to match the filter buttons.
function handleize(str) {
  return String(str || '')
    .toLowerCase()
    .replace(/\.com/g, '')
    .replace(/\.co\.uk/g, '')
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function escAttr(value) {
  return escapeHtml(value).replace(/'/g, '&#39;');
}

// Some metafields store their value as a small JSON wrapper (e.g. rating).
function unwrapValue(raw) {
  if (raw == null) return '';
  const s = String(raw).trim();
  if (s.startsWith('{') && s.includes('"value"')) {
    try {
      const obj = JSON.parse(s);
      if (obj && obj.value != null) return String(obj.value);
    } catch (_) {
      /* fall through */
    }
  }
  return s;
}

function fieldValue(node, key) {
  const f = node[key];
  return f && f.value != null ? f.value : '';
}

// Builds the same markup as snippets/review-card.liquid so existing CSS applies.
function buildReviewCardHtml(node, opts) {
  const author = fieldValue(node, 'customer_name') || 'Customer';
  const country = fieldValue(node, 'customer_country');
  const state = fieldValue(node, 'customer_state');
  let location = '';
  if (country && state) location = `${state}, ${country}`;
  else location = state || country;

  const source = fieldValue(node, 'review_source').trim();
  const sourceHandle = handleize(source);
  const sourceUrl = fieldValue(node, 'review_source_url');

  let rating = parseInt(unwrapValue(fieldValue(node, 'rating')) || '5', 10);
  if (isNaN(rating)) rating = 5;
  rating = Math.max(0, Math.min(5, rating));

  const title = fieldValue(node, 'review_title');
  const bodyRaw = fieldValue(node, 'review_comment');

  // Date
  let dateDisplay = '';
  let dateAttr = '';
  const rawDate = fieldValue(node, 'review_date');
  if (rawDate) {
    const d = new Date(rawDate);
    if (!isNaN(d)) {
      dateAttr = d.toISOString().slice(0, 10);
      dateDisplay = d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } else {
      dateDisplay = rawDate;
    }
  }

  // Product
  const productField = node.product_url || {};
  const productRef = productField.reference || {};
  const productName = fieldValue(node, 'product_name') || productRef.title || '';
  // Link straight to the linked product's page. onlineStoreUrl is null when a
  // product isn't published to the Online Store channel, so fall back to its
  // handle. When no product is linked on the metaobject entry we leave the name
  // as plain text — the product name alone is ambiguous (e.g. "Danish Modern
  // Teak Chest" matches several products), so guessing a URL would be wrong.
  const productUrl =
    productRef.onlineStoreUrl || (productRef.handle ? `/products/${productRef.handle}` : '');

  // Photos: picture_url (string) preferred, else rating_image file reference.
  const photos = [];
  const pictureUrl = fieldValue(node, 'picture_url');
  if (pictureUrl) {
    pictureUrl.split('|||||').forEach((u) => { if (u.trim()) photos.push(u.trim()); });
  }
  if (photos.length === 0) {
    const ri = node.rating_image && node.rating_image.reference;
    if (ri && ri.image && ri.image.url) photos.push(ri.image.url);
  }
  const hasPhotos = photos.length > 0;

  const verifiedRaw = unwrapValue(fieldValue(node, 'verified_purchase'));
  const verified = verifiedRaw === 'true' || verifiedRaw === '1';

  // Body: a rich-text field arrives as a JSON document; flag it for conversion.
  const bodyStr = String(bodyRaw || '').trim();
  const bodyIsJson = bodyStr.includes('"type":"root"') && bodyStr.includes('"children"');

  const key = node.handle || `${author}-${dateAttr}`;

  let stars = '';
  for (let i = 1; i <= 5; i++) {
    stars += i <= rating ? '<span class="star-filled">★</span>' : '<span class="star-empty">★</span>';
  }

  let sourceBadge = '';
  if (source) {
    if (sourceUrl) {
      sourceBadge = `<a href="${escAttr(sourceUrl)}" class="review-card__source review-card__source--${escAttr(sourceHandle)}" target="_blank" rel="noopener nofollow">${escapeHtml(source)}</a>`;
    } else {
      sourceBadge = `<span class="review-card__source review-card__source--${escAttr(sourceHandle)}">${escapeHtml(source)}</span>`;
    }
  }

  let locationHtml = '';
  if (location || verified) {
    locationHtml =
      `<p class="review-card__location">${location ? escapeHtml(location) : ''}` +
      `${verified ? '<span class="review-card__verified">Verified purchase</span>' : ''}</p>`;
  }

  let photosHtml = '';
  if (hasPhotos) {
    photosHtml =
      '<div class="review-card__photos">' +
      photos
        .map(
          (u) =>
            `<div class="review-card__photo"><img src="${escAttr(u)}" alt="${escAttr(author)}" class="review-card__photo-image" loading="lazy" width="400" height="400"></div>`
        )
        .join('') +
      '</div>';
  }

  let productHtml = '';
  if (productUrl || productName) {
    const inner = productUrl
      ? `<a href="${escAttr(productUrl)}" class="review-card__product-link">${escapeHtml(productName)}</a>`
      : `<span class="review-card__product-link">${escapeHtml(productName)}</span>`;
    productHtml =
      `<div class="review-card__product"><span class="review-card__product-label">Purchased</span>${inner}</div>`;
  }

  // The body text is inserted as-is when it is HTML; for a JSON document it is
  // placed escaped and flagged, so enhanceReviewCards converts it safely in the
  // same tick (before paint), which keeps the big concatenated innerHTML valid.
  const bodyInner = bodyIsJson ? escapeHtml(bodyStr) : bodyStr;
  const bodyFlag = bodyIsJson ? 'data-rich-json' : '';

  return (
    `<div class="review-card" data-source="${escAttr(sourceHandle)}" data-has-photos="${hasPhotos}" data-date="${escAttr(dateAttr)}" data-review-key="${escAttr(key)}">` +
    `<div class="review-card__header"><h3 class="review-card__author">${escapeHtml(author)}</h3>${sourceBadge}</div>` +
    `<div class="review-card__meta"><div class="review-card__stars">${stars}</div>` +
    `${dateDisplay ? `<span class="review-card__date">${escapeHtml(dateDisplay)}</span>` : ''}</div>` +
    locationHtml +
    `${title ? `<h4 class="review-card__title">${escapeHtml(title)}</h4>` : ''}` +
    `<div class="review-card__body"><div class="review-card__text rte" data-truncate-target ${bodyFlag}>${bodyInner}</div>` +
    `<button type="button" class="review-card__read-more" data-truncate-trigger style="display: none;">Read more</button></div>` +
    photosHtml +
    productHtml +
    '</div>'
  );
}

// Fetches every review via the Storefront API and rebuilds the grid. Returns
// true on success (grid repopulated), false to fall back to server-rendered.
async function loadAllViaStorefront(grid) {
  const token = grid.getAttribute('data-sf-token');
  const domain = grid.getAttribute('data-shop-domain');
  const type = grid.getAttribute('data-metaobject-type') || 'productreviews';
  const version = grid.getAttribute('data-sf-version') || '2024-10';
  if (!token || !domain) return false;

  const endpoint = `https://${domain}/api/${version}/graphql.json`;
  const query = `
    query Reviews($type: String!, $cursor: String) {
      metaobjects(type: $type, first: 250, after: $cursor) {
        pageInfo { hasNextPage endCursor }
        nodes {
          handle
          customer_name: field(key: "customer_name") { value }
          customer_country: field(key: "customer_country") { value }
          customer_state: field(key: "customer_state") { value }
          review_source: field(key: "review_source") { value }
          review_source_url: field(key: "review_source_url") { value }
          rating: field(key: "rating") { value }
          review_title: field(key: "review_title") { value }
          review_comment: field(key: "review_comment") { value }
          review_date: field(key: "review_date") { value }
          product_name: field(key: "product_name") { value }
          product_url: field(key: "product_url") { value reference { ... on Product { title handle onlineStoreUrl } } }
          picture_url: field(key: "picture_url") { value }
          rating_image: field(key: "rating_image") { value reference { ... on MediaImage { image { url } } } }
          verified_purchase: field(key: "verified_purchase") { value }
        }
      }
    }`;

  const nodes = [];
  let cursor = null;
  try {
    for (let guard = 0; guard < 50; guard++) {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({ query, variables: { type, cursor } }),
      });
      if (!res.ok) return false;
      const json = await res.json();
      const mo = json && json.data && json.data.metaobjects;
      if (!mo) return false;
      nodes.push(...mo.nodes);
      if (mo.pageInfo && mo.pageInfo.hasNextPage) {
        cursor = mo.pageInfo.endCursor;
      } else {
        break;
      }
    }
  } catch (_) {
    return false;
  }

  if (nodes.length === 0) return false;

  // Keep any hand-added block cards already on the page (not in the metaobject).
  const blockCards = Array.from(grid.querySelectorAll('.review-card'))
    .filter((c) => (c.getAttribute('data-review-key') || '').startsWith('block-'))
    .map((c) => c.outerHTML);

  grid.innerHTML = nodes.map((n) => buildReviewCardHtml(n)).join('') + blockCards.join('');
  return true;
}

// The section renders only the first server page (up to 250 reviews). To filter
// and paginate across the whole catalogue in the browser, fetch the remaining
// server pages and append their cards, so every review is in the DOM.
async function loadRemainingPages(grid) {
  const totalPages = parseInt(grid.getAttribute('data-total-pages'), 10) || 1;
  const currentPage = parseInt(grid.getAttribute('data-current-page'), 10) || 1;
  if (totalPages <= 1) return;

  // Track keys already in the DOM so a page returned twice can't duplicate.
  const seen = new Set();
  grid.querySelectorAll('.review-card').forEach((card) => {
    const key = card.getAttribute('data-review-key');
    if (key) seen.add(key);
  });

  const basePath = window.location.pathname;
  let added = 0;

  for (let page = 1; page <= totalPages; page++) {
    if (page === currentPage) continue;
    try {
      // Plain full-page fetch of ?page=N. On the live storefront Shopify serves
      // the next slice of reviews here; we parse out its cards and append them.
      // (The Section Rendering API is avoided — it ignores pagination.)
      const params = new URLSearchParams(window.location.search);
      params.set('page', String(page));
      const url = `${basePath}?${params.toString()}`;
      const res = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!res.ok) continue;
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fetchedGrid = doc.querySelector('#ReviewsGrid');
      if (!fetchedGrid) continue;
      fetchedGrid.querySelectorAll('.review-card').forEach((card) => {
        const key = card.getAttribute('data-review-key');
        // Skip any review already on the page (guards against overlap/dupes).
        if (key && seen.has(key)) return;
        if (key) seen.add(key);
        grid.appendChild(card);
        added++;
      });
    } catch (_) {
      // A failed page just means those reviews stay unfetched; keep going.
    }
  }
  return added;
}

async function initReviewsGrid(scope) {
  const root = scope || document;

  const grid = root.querySelector('#ReviewsGrid');
  if (!grid) {
    enhanceReviewCards(root);
    return;
  }

  // Failsafe: whatever happens with the network, never leave the loader up for
  // more than a few seconds — reveal whatever is already in the DOM.
  const loaderFailsafe = setTimeout(() => revealReviews(root, grid), 8000);

  const serverPaginated = grid.getAttribute('data-server-paginated') === 'true';

  // Pull in every other server page before wiring up the interactions, so the
  // filter and pagination operate over all reviews, not just the first page.
  if (serverPaginated && grid.dataset.allLoaded !== 'true') {
    grid.dataset.allLoaded = 'true';
    // Remove the no-JS server pagination entirely; the JS pagination below is
    // the single source of truth, so the two navs can never both show.
    const serverNav = root.querySelector('#ReviewsServerPagination');
    if (serverNav) serverNav.remove();

    // Preferred: load ALL reviews via the Storefront API. If no token is set or
    // the request fails, fall back to fetching the extra server pages.
    const loadedAll = await loadAllViaStorefront(grid);
    if (!loadedAll) {
      await loadRemainingPages(grid);
    }
  }

  // Must run before the clamp below measures anything. Covers the fetched cards.
  enhanceReviewCards(root);

  // Pagination & Filter Logic
  const filterBtns = root.querySelectorAll('.reviews-filter__btn');
  let allCards = Array.from(grid.querySelectorAll('.review-card'));
  let paginationContainer = root.querySelector('#ReviewsPagination');
  if (!paginationContainer) {
    paginationContainer = document.createElement('div');
    paginationContainer.className = 'reviews-pagination';
    paginationContainer.id = 'ReviewsPagination';
    grid.insertAdjacentElement('afterend', paginationContainer);
  }

  const reviewsPerPage = parseInt(grid.getAttribute('data-per-page')) || 6;

  // Ordering runs here rather than in Liquid, where sorting metaobject entries
  // by a date field is not reliable. Cards carry the date as data-date.
  const sortOrder = grid.getAttribute('data-sort') || 'entered';
  if (sortOrder === 'newest' || sortOrder === 'oldest') {
    const direction = sortOrder === 'newest' ? -1 : 1;
    allCards
      .slice()
      .sort((a, b) => {
        const dateA = a.getAttribute('data-date') || '';
        const dateB = b.getAttribute('data-date') || '';
        // Undated reviews sit at the end either way.
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        return dateA < dateB ? direction : dateA > dateB ? -direction : 0;
      })
      .forEach((card) => grid.append(card));
    allCards = Array.from(grid.querySelectorAll('.review-card'));
  }

  let currentPage = 1;
  let currentFilter = 'all';
  let filteredCards = [...allCards];

  function updateGrid() {
    // 1. Filter cards based on currentFilter
    filteredCards = allCards.filter(card => {
      const source = card.getAttribute('data-source');
      const hasPhotos = card.getAttribute('data-has-photos');
      
      if (currentFilter === 'all') return true;
      if (currentFilter === 'photos') return hasPhotos === 'true';
      return source === currentFilter;
    });

    const totalPages = Math.ceil(filteredCards.length / reviewsPerPage);
    if (currentPage > totalPages) currentPage = 1;

    // 2. Hide all cards initially
    allCards.forEach(card => card.classList.add('is-hidden'));

    // 3. Show only cards for the current page
    const startIndex = (currentPage - 1) * reviewsPerPage;
    const endIndex = startIndex + reviewsPerPage;
    
    filteredCards.slice(startIndex, endIndex).forEach(card => {
      card.classList.remove('is-hidden');
    });

    renderPagination(totalPages);
  }

  function renderPagination(totalPages) {
    paginationContainer.innerHTML = '';
    paginationContainer.hidden = totalPages <= 1;

    if (totalPages <= 1) return; // Hide pagination if only 1 page

    // Prev Button
    const prevBtn = document.createElement('button');
    prevBtn.className = 'reviews-pagination__btn reviews-pagination__btn--prev';
    prevBtn.disabled = currentPage === 1;
    prevBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>';
    prevBtn.addEventListener('click', () => {
      if (currentPage > 1) {
        currentPage--;
        updateGrid();
        grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
    paginationContainer.appendChild(prevBtn);

    // Number Buttons — windowed so a few hundred reviews don't print hundreds
    // of buttons: first, last, and a small range either side of the current.
    const addPageBtn = (i) => {
      const btn = document.createElement('button');
      btn.className = `reviews-pagination__btn ${i === currentPage ? 'is-active' : ''}`;
      btn.textContent = i;
      btn.addEventListener('click', () => {
        currentPage = i;
        updateGrid();
        grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      paginationContainer.appendChild(btn);
    };
    const addEllipsis = () => {
      const span = document.createElement('span');
      span.className = 'reviews-pagination__btn reviews-pagination__btn--ellipsis';
      span.textContent = '…';
      paginationContainer.appendChild(span);
    };

    const windowSize = 2; // pages shown either side of the current one
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= currentPage - windowSize && i <= currentPage + windowSize)
      ) {
        pages.push(i);
      }
    }
    let last = 0;
    pages.forEach((i) => {
      if (i - last > 1) addEllipsis();
      addPageBtn(i);
      last = i;
    });

    // Next Button
    const nextBtn = document.createElement('button');
    nextBtn.className = 'reviews-pagination__btn reviews-pagination__btn--next';
    nextBtn.disabled = currentPage === totalPages;
    nextBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>';
    nextBtn.addEventListener('click', () => {
      if (currentPage < totalPages) {
        currentPage++;
        updateGrid();
        grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
    paginationContainer.appendChild(nextBtn);
  }

  filterBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      filterBtns.forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');

      currentFilter = btn.getAttribute('data-filter');
      currentPage = 1; // Reset to page 1 on filter change
      updateGrid();
    });
  });

  // Initialize
  updateGrid();

  // Everything is fetched, converted and paginated — reveal the grid and drop
  // the loader, so the raw-JSON stage is never visible.
  clearTimeout(loaderFailsafe);
  revealReviews(root, grid);
}

/** Hides the loader and shows the grid. Safe to call more than once. */
function revealReviews(root, grid) {
  if (grid) grid.classList.remove('is-loading');
  const loader = (root || document).querySelector('[data-reviews-loader]');
  if (loader) loader.hidden = true;
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initReviewsGrid());
} else {
  initReviewsGrid();
}

// Theme editor: re-init when the section is re-rendered
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('#ReviewsGrid')) initReviewsGrid(event.target);
});
